/**
 * Pure helpers for skip-logic rule creation and evaluation.
 *
 * Kept as standalone functions (not hooks) so both the rule editor modal
 * and the preview evaluator can share them and unit tests can import them
 * without rendering a React tree.
 */

/**
 * Pick the element in a set whose screen appears latest in the traversal.
 * Falls back to the first id if none resolve in allQuestionElements.
 *
 * @param {string[]} elementIds
 * @param {{id: string, screenIndex: number}[]} allQuestionElements
 * @returns {string | null}
 */
export const latestByScreen = (elementIds, allQuestionElements) => {
  if (!elementIds || elementIds.length === 0) return null;
  let best = null;
  let bestIdx = -1;
  for (const id of elementIds) {
    const el = allQuestionElements.find((q) => q.id === id);
    const idx = el?.screenIndex ?? -1;
    if (idx > bestIdx) {
      bestIdx = idx;
      best = id;
    }
  }
  return best || elementIds[0];
};

/**
 * Decide which element a rule should be attached to. Cumulative and
 * combination rules must bind to the LAST contributing question —
 * otherwise the rule is invisible to the evaluator when that last
 * question sits on a later screen than the bind.
 *
 * @param {object} params
 * @param {string} params.conditionType - 'answer_value' | 'cumulative_score' | 'answer_combination'
 * @param {string[]} [params.questionIds] - element IDs referenced by cumulative rules
 * @param {{question_id: string}[]} [params.conditions] - sub-conditions for combination rules
 * @param {string} [params.selectedElement] - element currently selected in the UI
 * @param {{id: string, screenIndex: number}[]} params.allQuestionElements
 * @returns {string | null | undefined}
 */
export const selectRuleAttachPoint = ({
  conditionType,
  questionIds,
  conditions,
  selectedElement,
  allQuestionElements,
}) => {
  if (conditionType === 'cumulative_score') {
    return latestByScreen(questionIds || [], allQuestionElements);
  }
  if (conditionType === 'answer_combination') {
    const combIds = (conditions || []).map((c) => c.question_id).filter(Boolean);
    return latestByScreen(combIds, allQuestionElements) || selectedElement;
  }
  return selectedElement || questionIds?.[0];
};

/**
 * Get the response key for an element — matches ScreenRenderer behavior.
 */
export const getResponseKey = (element) =>
  element.type === 'question' ? element.questionId || element.id : element.id;

/**
 * Coerce operands and apply a comparison operator. Returns false for
 * unknown operators or unparseable numeric comparisons.
 */
export const compareValues = (answer, operator, targetValue) => {
  const numAnswer = parseFloat(answer);
  const numTarget = parseFloat(targetValue);
  switch (operator) {
    case '>=':
      return numAnswer >= numTarget;
    case '<=':
      return numAnswer <= numTarget;
    case '>':
      return numAnswer > numTarget;
    case '<':
      return numAnswer < numTarget;
    case '==':
    case '=':
      // eslint-disable-next-line eqeqeq
      return numAnswer == numTarget;
    case '!=':
      // eslint-disable-next-line eqeqeq
      return numAnswer != numTarget;
    default:
      return false;
  }
};

/**
 * Evaluate a single skip-logic rule against the current response set.
 *
 * @param {object} rule - rule object (frontend shape)
 * @param {object} context
 * @param {object[]} context.allElements - all question elements across screens
 * @param {object} context.responses - dict keyed by element.questionId (or fallback id)
 * @param {object} [context.currentElement] - element the rule is attached to (for answer_value)
 * @returns {boolean} true if the rule's condition matched
 */
export const evaluateRuleCondition = (rule, { allElements, responses, currentElement }) => {
  if (rule.enabled === false) return false;

  const findElement = (qId) => {
    let el = allElements.find((e) => e.id === qId);
    if (!el) el = allElements.find((e) => String(e.questionId) === String(qId));
    return el;
  };

  if (rule.condition_type === 'answer_value') {
    const answer = currentElement ? responses[getResponseKey(currentElement)] : undefined;
    if (answer === undefined) return false;
    return compareValues(answer, rule.operator, rule.value);
  }

  if (rule.condition_type === 'cumulative_score') {
    const questionIds = rule.question_ids || rule.backend_question_ids || [];
    if (questionIds.length === 0) return false;
    let total = 0;
    for (const qId of questionIds) {
      const el = findElement(qId);
      const answer = el ? responses[getResponseKey(el)] : undefined;
      if (answer === undefined) return false; // defer — not all answered
      total += parseFloat(answer) || 0;
    }
    return compareValues(total, rule.operator, rule.value);
  }

  if (rule.condition_type === 'answer_combination') {
    const subConditions = rule.conditions || rule.backend_conditions || [];
    if (subConditions.length === 0) return false;
    const logic = rule.logic || 'and';
    const results = [];
    for (const cond of subConditions) {
      const el = findElement(cond.question_id);
      const answer = el ? responses[getResponseKey(el)] : undefined;
      if (answer === undefined) return false; // defer
      results.push(compareValues(answer, cond.operator, cond.value));
    }
    return logic === 'or' ? results.some((r) => r) : results.every((r) => r);
  }

  return false;
};

/**
 * Detect potential cycles in the skip-rule graph. Treats each
 * `screen_question_id` as a node and each `skip_to` rule as a directed
 * edge from the rule's attach-point screen to its target screen. Returns
 * the screens participating in the first cycle found, or null.
 *
 * This only catches STATIC cycles — rules that unconditionally chain back
 * to themselves. State-dependent cycles (rule fires only when response X)
 * would require symbolic evaluation; the runtime loop cap is the safety
 * net for those.
 *
 * @param {Record<string, object[]>} rulesByElement
 * @param {{id: string, screenIndex: number}[]} allQuestionElements
 * @param {{id: string|number, name: string}[]} screens
 * @returns {{ path: string[] } | null}  Screen names in the cycle, or null
 */
export const detectSkipRuleCycles = (rulesByElement, allQuestionElements, screens) => {
  // Build adjacency list: sourceScreenIdx -> [targetScreenIdx, ...]
  const edges = new Map();
  const addEdge = (from, to) => {
    if (from == null || to == null || from === to) return;
    if (!edges.has(from)) edges.set(from, new Set());
    edges.get(from).add(to);
  };

  for (const [elementId, rules] of Object.entries(rulesByElement || {})) {
    const el = allQuestionElements.find((e) => e.id === elementId);
    const fromIdx = el?.screenIndex;
    if (fromIdx == null) continue;
    for (const rule of rules || []) {
      if (rule.enabled === false) continue;
      if (rule.action_type !== 'skip_to') continue;
      let toIdx = screens.findIndex((s) => String(s.id) === String(rule.target_screen_id));
      if (toIdx === -1 && rule.target_screen_name) {
        toIdx = screens.findIndex((s) => s.name === rule.target_screen_name);
      }
      if (toIdx === -1 && rule.target_screen_index !== undefined) {
        toIdx = rule.target_screen_index;
      }
      if (toIdx !== -1) addEdge(fromIdx, toIdx);
    }
  }

  // DFS each node, walking with a path stack so we can report the cycle.
  const WHITE = 0;
  const GRAY = 1;
  const BLACK = 2;
  const color = new Map();
  const parent = new Map();

  for (const start of edges.keys()) {
    if (color.get(start) === BLACK) continue;
    const stack = [{ node: start, iter: edges.get(start)?.values() }];
    color.set(start, GRAY);
    while (stack.length) {
      const frame = stack[stack.length - 1];
      const next = frame.iter?.next();
      if (next?.done || !frame.iter) {
        color.set(frame.node, BLACK);
        stack.pop();
        continue;
      }
      const child = next.value;
      const c = color.get(child) ?? WHITE;
      if (c === GRAY) {
        // Cycle found — reconstruct path from child back around.
        const cycle = [child];
        let walker = frame.node;
        while (walker !== child && walker != null) {
          cycle.push(walker);
          walker = parent.get(walker);
        }
        cycle.push(child);
        cycle.reverse();
        return { path: cycle.map((idx) => screens[idx]?.name || `Screen ${idx + 1}`) };
      }
      if (c === WHITE) {
        color.set(child, GRAY);
        parent.set(child, frame.node);
        stack.push({ node: child, iter: edges.get(child)?.values() });
      }
    }
  }

  return null;
};

/**
 * Walk every rule across every element and return the first one whose
 * condition is met. Rules are iterated in deterministic order — by the
 * screenIndex of their attach element, then by rule-index within that list.
 *
 * Cumulative and combination rules fire the moment every contributing
 * question has a response (thanks to defer-on-missing semantics in
 * evaluateRuleCondition), which decouples them from attach-point. That
 * makes non-linear flows — skip-back, skip-forward, arbitrary ordering —
 * Just Work, because the rule no longer depends on the participant
 * visiting the attached screen last.
 *
 * answer_value rules still effectively require their attached question's
 * response to be present, so they defer until the participant answers it.
 *
 * @param {Record<string, object[]>} rulesByElement - navigationRules state
 * @param {object} context
 * @param {object[]} context.allElements
 * @param {object} context.responses
 * @returns {{rule: object, attachElementId: string} | null}
 */
export const findFirstMatchingRule = (rulesByElement, { allElements, responses, excludeRuleIds }) => {
  const entries = Object.entries(rulesByElement || {});
  entries.sort(([aId], [bId]) => {
    const a = allElements.find((e) => e.id === aId)?.screenIndex ?? Infinity;
    const b = allElements.find((e) => e.id === bId)?.screenIndex ?? Infinity;
    return a - b;
  });

  const skip = excludeRuleIds instanceof Set ? excludeRuleIds : new Set(excludeRuleIds || []);

  for (const [elementId, rules] of entries) {
    if (!rules || rules.length === 0) continue;
    const currentElement = allElements.find((e) => e.id === elementId);
    for (const rule of rules) {
      if (skip.has(rule.id)) continue;
      if (evaluateRuleCondition(rule, { allElements, responses, currentElement })) {
        return { rule, attachElementId: elementId };
      }
    }
  }
  return null;
};
